// Keep the native gallery, variant media isolation and image zoom in one place.
(() => {
  if (window.trobartProductGallery) {
    window.trobartProductGallery.init();
    return;
  }

  const gallerySelector = 'media-gallery[data-trobart-gallery]';
  const slideSelector = '.product__media-list > li[data-media-id]';
  const controllers = new Map();
  const editableSelector = 'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="textbox"], video, audio, iframe, model-viewer';

  function create(gallery) {
    let activeId = '';
    let pending = false;
    let preferNativeActive = true;
    let disposed = false;
    const slides = () => [...gallery.querySelectorAll(slideSelector)]
      .filter(slide => !slide.hidden && slide.style.display !== 'none');

    function render(available, active) {
      const current = active ? available.indexOf(active) + 1 : 0;
      const thumbnails = new Map([...gallery.querySelectorAll('.thumbnail-list > li[data-target]')]
        .map(thumbnail => [thumbnail.dataset.target, thumbnail]));
      available.forEach((slide, index) => {
        const thumbnail = thumbnails.get(slide.dataset.mediaId);
        if (!thumbnail) return;
        const ordinal = String(index + 1);
        if (thumbnail.dataset.mediaPosition !== ordinal) thumbnail.dataset.mediaPosition = ordinal;
        const media = slide.querySelector('.product-media-container');
        const type = media?.classList.contains('media-type-model') ? 'tbModelLabel' :
          media?.classList.contains('media-type-video') || media?.classList.contains('media-type-external_video') ?
            'tbVideoLabel' : 'tbImageLabel';
        const template = gallery.dataset[type];
        const button = thumbnail.querySelector('button');
        if (!template || !button) return;
        const label = template.replace(/\[index\]/g, ordinal);
        if (button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
      });
      gallery.querySelectorAll('[data-tb-gallery-toolbar]').forEach(toolbar => {
        if (toolbar.hidden) toolbar.hidden = false;
      });
      gallery.querySelectorAll('[data-tb-gallery-current]').forEach(output => {
        if (output.textContent !== String(current)) output.textContent = String(current);
      });
      gallery.querySelectorAll('[data-tb-gallery-total]').forEach(output => {
        if (output.textContent !== String(available.length)) output.textContent = String(available.length);
      });
      gallery.querySelectorAll('[data-tb-gallery-step]').forEach(button => {
        button.disabled = available.length <= 1;
      });
    }

    function sync(preferNative = false, selectedId) {
      if (disposed) return;
      const available = slides();
      const marked = available.find(slide => slide.classList.contains('is-active'));
      const remembered = available.find(slide => slide.dataset.mediaId === (selectedId || activeId));
      const active = selectedId ? remembered : preferNative ? marked || remembered : remembered || marked;
      const selected = active || available[0];
      activeId = selected?.dataset.mediaId || '';
      // Filtering can remove the selected photograph before the native variant
      // event arrives. Select an allowed photograph rather than leave a blank.
      if (selected && !selectedId && !selected.classList.contains('is-active')) {
        gallery.setActiveMedia?.(activeId, false);
      }
      render(available, selected);
    }

    function schedule(nativeChanged) {
      preferNativeActive ||= nativeChanged;
      if (pending || disposed) return;
      pending = true;
      queueMicrotask(() => {
        pending = false;
        if (disposed) return;
        const preferNative = preferNativeActive;
        preferNativeActive = false;
        sync(preferNative);
      });
    }

    function navigate(step) {
      const available = slides();
      if (available.length < 2) {
        sync();
        return;
      }
      const current = Math.max(0, available.findIndex(slide => slide.dataset.mediaId === activeId));
      const next = available[(current + step + available.length) % available.length];
      activeId = next.dataset.mediaId;
      gallery.setActiveMedia?.(activeId, false);
      render(available, next);
    }

    function click(event) {
      const button = event.target.closest?.('[data-tb-gallery-step]');
      if (!button || button.closest(gallerySelector) !== gallery || button.disabled) return;
      const step = Number(button.dataset.tbGalleryStep);
      if (step !== -1 && step !== 1) return;
      event.preventDefault();
      navigate(step);
    }

    function keydown(event) {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey ||
          event.target.closest?.(gallerySelector) !== gallery || event.target.closest?.(editableSelector)) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (slides().length < 2) return;
      event.preventDefault();
      navigate(event.key === 'ArrowLeft' ? -1 : 1);
    }

    function slideChanged(event) {
      const current = event.detail?.currentElement;
      if (!current?.matches?.(slideSelector) || !gallery.contains(current) || current.hidden ||
          current.style.display === 'none') return;
      // Native mobile sliders emit a non-bubbling event; capture it without
      // activating media again, which would restart the scroll animation.
      sync(false, current.dataset.mediaId);
    }

    function includesMediaList(node) {
      return node.nodeType === 1 && (node.matches('.product__media-list') ||
        Boolean(node.querySelector('.product__media-list')));
    }

    const observer = new MutationObserver(records => {
      let relevant = false;
      let nativeChanged = false;
      for (const record of records) {
        if (record.type === 'attributes') {
          if (!record.target.matches(slideSelector)) continue;
          relevant = true;
          nativeChanged ||= record.attributeName === 'class';
        } else if (record.target.matches?.('.product__media-list') ||
                   [...record.addedNodes, ...record.removedNodes].some(includesMediaList)) {
          relevant = true;
        }
      }
      // Counter text and toolbar state are deliberately excluded, preventing
      // observer feedback when render updates the accessible status.
      if (relevant) schedule(nativeChanged);
    });
    observer.observe(gallery, {
      subtree: true, childList: true, attributes: true, attributeFilter: ['hidden', 'style', 'class'],
    });
    gallery.addEventListener('click', click);
    gallery.addEventListener('keydown', keydown);
    gallery.addEventListener('slideChanged', slideChanged, true);
    sync(true);
    preferNativeActive = false;

    return {
      refresh() { sync(true); },
      destroy() {
        disposed = true;
        observer.disconnect();
        gallery.removeEventListener('click', click);
        gallery.removeEventListener('keydown', keydown);
        gallery.removeEventListener('slideChanged', slideChanged, true);
      },
    };
  }

  function init() {
    for (const [gallery, controller] of controllers) {
      if (gallery.isConnected) continue;
      controller.destroy();
      controllers.delete(gallery);
    }
    document.querySelectorAll(gallerySelector).forEach(gallery => {
      if (!controllers.has(gallery)) controllers.set(gallery, create(gallery));
      else controllers.get(gallery).refresh();
    });
  }

  window.trobartProductGallery = { init };
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', event => {
    for (const [gallery, controller] of controllers) {
      if (event.target !== gallery && !event.target.contains?.(gallery)) continue;
      controller.destroy();
      controllers.delete(gallery);
    }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
